import { buildComboObjectForMatching, convertedCurrValue, isSameCombo, pickSectionSum, resolveColourwayId, roundTo, sumSectionValues, toFiniteNumber, toIdString } from './primitives';
import type {
  CalcContext,
  CalcMasters,
  CombinationRow,
  CostingTemplateForCalc,
  DistributionCentreLanded,
  ExchangeRateEntry,
  ExchangeRateSet,
  GrandTotal,
  LandedExchangeRateInformation,
  LandedRecomputeContext,
  LandedRow,
  Ref,
  RoyaltyMaster,
  TemplateColumnDef,
} from './types';
import { FREIGHT_TOGGLE_ALIASES } from '../../../../Library/CostingTemplateTSX/freight-toggle.helpers';

/* The landed chain: default columns (Duty/Tariff/Freight/Agent/Royalty) then bottom columns
   (Selling Price Net, Gross Profit, Markup, Margin, Estimated Profit, Retail Margin).

   SEMANTICS: this port follows the DETAIL PAGE (useDetailedPageHook.js), not the backend engine.
   The two disagree in three confirmed places, and the grid must agree with the page a user can open
   in the next tab:

   1. Zero-freight duty/tariff — the detail page charges duty on (landedCost - testing/setup deduction)
      when a row has no freight at all; the backend zeroes it and subtracts the previous value.
      (useDetailedPageHook.js:918-928 vs product-costing-calcaution-service.js:953-973)
   2. Royalty fees — the detail page re-resolves the rate from the license/likeness master by the
      costing's incoterm and applies it only when the product carries that id; the backend trusts
      whatever rate is stored on the row and applies it ungated.
      (useDetailedPageHook.js:1019-1049 vs product-costing-calcaution-service.js:1022-1028)
   3. Agent fee — the detail page reads the rate from the supplier master and writes it back onto the
      row; the backend derives it from the row's stored value. (L934 vs L974-976) */

/** Percentage default columns whose base includes freight; the rest are simple rates on landed cost. */
const FREIGHT_BASED_ALIASES = ['dutyPercentage', 'tariffCostPercentage'];

/**
 * Resolves a distribution centre's default exchange rate from a season's rate list: the entry whose
 * defaultCurrency is the DC currency, then the combination whose currency is the supplier currency.
 * @returns the rate, or null when any part of the lookup is missing so the caller can fall back
 */
export function resolveDcDefaultRate(exchange: LandedExchangeRateInformation | undefined, exchangeRateList: ExchangeRateEntry[] | undefined): number | null {
  if (!Array.isArray(exchangeRateList) || exchangeRateList.length === 0) return null;
  const dcCurrency = exchange?.companyCurrency;
  const supplierCurrency = exchange?.supplierCurrency;
  if (!dcCurrency || !supplierCurrency) return null;
  const matched = exchangeRateList.find((entry) => entry?.defaultCurrency === dcCurrency);
  const found = matched?.combinationCurrencies?.find((combination) => combination?.currency === supplierCurrency);
  return found?.value != null ? toFiniteNumber(found.value) : null;
}

/**
 * Resolves a distribution centre's default rate for a supplier currency, never returning zero.
 *
 * `resolveDcDefaultRate` reports "no rate" as null so readers can tell an unconfigured combination
 * from a real one. This wrapper is for the WRITE path, which has to persist a usable number: a blank
 * master value parses to 0, and 0 is treated as "no conversion" downstream, so it collapses to 1
 * rather than storing a rate that silently voids every converted figure.
 */
export function resolveDcDefaultCurrValue(exchangeRateList: ExchangeRateEntry[] | undefined, dcCurrency: string | undefined, supplierCurrency: string | undefined): number {
  if (!Array.isArray(exchangeRateList) || exchangeRateList.length === 0) return 1;
  if (!dcCurrency || !supplierCurrency) return 1;
  const rate = resolveDcDefaultRate({ companyCurrency: dcCurrency, supplierCurrency }, exchangeRateList);
  return rate && rate > 0 ? rate : 1;
}

/**
 * Resolves a distribution centre's own base value from the master entry for its currency.
 *
 * Looked up rather than hardcoded so the value follows the master, and so nothing depends on the
 * document-root `defaultCurrencyValue` the multiple-currencies migration moved onto each entry.
 */
export function resolveCompanyCurrencyValue(exchangeRateList: ExchangeRateEntry[] | undefined, dcCurrency: string | undefined): number {
  if (!Array.isArray(exchangeRateList) || exchangeRateList.length === 0 || !dcCurrency) return 1;
  const matched = exchangeRateList.find((entry) => entry?.defaultCurrency === dcCurrency);
  return toFiniteNumber(matched?.defaultCurrencyValue) || 1;
}

/**
 * Rebases one distribution centre's exchange-rate block onto a newly selected supplier.
 *
 * A rate typed against the previous supplier's currency means nothing once the supplier changes, so
 * the manual toggle is switched off and the rate is re-seeded from the season master for the new
 * supplier currency — the behaviour `rebaseExchangeInfoForSupplier` already gives the detail page.
 *
 * This replaces clearing the block to `{}`. That clear stripped the very `companyCurrency` /
 * `supplierCurrency` pair `resolveDcDefaultRate` needs, so the lookup returned null, the rate fell
 * back to 1, and the collapse was persisted — leaving the costing permanently unconverted.
 */
export function rebaseExchangeInfoForSupplier(
  exchangeInfo: LandedExchangeRateInformation | undefined,
  dcCurrency: string | undefined,
  supplierCurrency: string | undefined,
  exchangeRateList: ExchangeRateEntry[] | undefined,
): LandedExchangeRateInformation {
  const rate = resolveDcDefaultCurrValue(exchangeRateList, dcCurrency, supplierCurrency);
  /* The currency keys are spread in only when known: react_app compiles with
     `exactOptionalPropertyTypes`, which rejects an explicit `undefined` on an optional property. */
  return {
    ...(exchangeInfo ?? {}),
    ...(dcCurrency === undefined ? {} : { companyCurrency: dcCurrency }),
    ...(supplierCurrency === undefined ? {} : { supplierCurrency }),
    manualAdded: false,
    exchangedRate: rate,
    /* Re-seeded too, not just carried over: the detail page prefers it as its fallback rate, so a
       value left over from the previous supplier would resurface there. */
    exchangedRateManualFalse: rate,
    companyCurrencyValue: resolveCompanyCurrencyValue(exchangeRateList, dcCurrency),
  };
}

/**
 * Resolves the retail exchange rate (sub-brand currency to distribution-centre currency).
 * Mirrors useDetailedPageHook.getRetailExRate; defaults to 1 when anything is missing.
 */
export function resolveRetailExRate(subBrandCurrency: string | null | undefined, dcCurrency: string | undefined, exchangeRateList: ExchangeRateEntry[] | undefined): number {
  if (!Array.isArray(exchangeRateList) || exchangeRateList.length === 0) return 1;
  if (!subBrandCurrency || !dcCurrency) return 1;
  const matched = exchangeRateList.find((entry) => entry?.defaultCurrency === subBrandCurrency);
  const found = matched?.combinationCurrencies?.find((combination) => combination?.currency === dcCurrency);
  return toFiniteNumber(found?.value) || 1;
}

/** Reads the exchange-rate set for a costing's season and year out of the page-level masters. */
export function getExchangeRateSet(context: CalcContext, masters: CalcMasters | undefined): ExchangeRateSet | undefined {
  if (!context.seasonId || !context.year) return undefined;
  return masters?.exchangeRatesBySeasonYear?.[`${context.seasonId}_${context.year}`];
}

/** Resolves the costing's sub-brand currency from the page-level sub-brand master list. */
export function getSubBrandCurrency(context: CalcContext, masters: CalcMasters | undefined): string | null {
  if (!context.subBrandId) return null;
  /* Compared as strings to stay identical to the server engine, where a master read through
     mongoose carries a bson ObjectId `_id` that a raw `===` never matches. */
  const wantedSubBrandId = toIdString(context.subBrandId as unknown as Ref | string | null);
  return masters?.subBrandList?.find((subBrand) => toIdString(subBrand?._id as unknown as Ref | string | null) === wantedSubBrandId)?.currency ?? null;
}

/**
 * Builds the per-distribution-centre context every landed row of that DC shares:
 * section sums, exchange rates and the retail rate.
 */
export function buildLandedRecomputeContext(
  context: CalcContext,
  exchange: LandedExchangeRateInformation | undefined,
  masters: CalcMasters | undefined,
): LandedRecomputeContext {
  const colourWayToggle = Boolean(context.colourWayToggle);
  const rateSet = getExchangeRateSet(context, masters);
  const exchangeInfo = exchange ?? context.landedExchangeRateInformation ?? {};

  const storedExchangedRate = toFiniteNumber(exchangeInfo.exchangedRate) || 1;
  const dcDefaultRate = resolveDcDefaultRate(exchangeInfo, rateSet?.exchangeRateList);
  const exchangedRate = exchangeInfo?.manualAdded ? storedExchangedRate : dcDefaultRate || storedExchangedRate;
  const companyCurrencyValue = toFiniteNumber(exchangeInfo.companyCurrencyValue) || 1;
  const subBrandCurrency = getSubBrandCurrency(context, masters);

  return {
    testingSum: sumSectionValues(context.testing?.testingInfo, colourWayToggle, 'pricePerUnit'),
    setupSum: sumSectionValues(context.additional?.additionalInfo, colourWayToggle, 'totalPrice'),
    colourWayToggle,
    primaryCurrencyValue: exchangedRate || 1,
    secondaryCurrencyValue: companyCurrencyValue || 1,
    retailExRate: resolveRetailExRate(subBrandCurrency, exchangeInfo.companyCurrency, rateSet?.exchangeRateList),
    hasRetailCurrency: Boolean(subBrandCurrency),
    agentFee: toFiniteNumber(context.agentFee),
  };
}

/**
 * Stamps each landed row with the incoterm price and quantity from its matching grand-total row.
 * Rows with no match keep their stored figures, mirroring grandTotalLandedInfoCalculation.
 */
export function applyGrandTotalToLandedRows(rows: LandedRow[], grandTotal: GrandTotal, context: CalcContext, recomputeContext: LandedRecomputeContext, lengthsPresent: boolean): LandedRow[] {
  const grandRows: CombinationRow[] = (grandTotal.grandTotalInfo ?? []).flatMap((group) => group.expandData ?? []);
  if (grandRows.length === 0) return rows;

  const colourWayToggle = Boolean(context.colourWayToggle);
  const sizeLengthToggle = Boolean(context.sizeLengthToggle);

  return rows.map((row) => {
    const rowCombo = buildComboObjectForMatching(row, colourWayToggle, sizeLengthToggle, lengthsPresent);
    const matched = grandRows.find((grandRow) => isSameCombo(rowCombo, buildComboObjectForMatching(grandRow, colourWayToggle, sizeLengthToggle, lengthsPresent)));
    if (!matched) return { ...row };

    const pricePerUnitIncoterm = toFiniteNumber(matched.pricePerUnit);
    const totalIncoterm = toFiniteNumber(matched.totalPrice);
    return {
      ...row,
      pricePerUnitInco: String(pricePerUnitIncoterm.toFixed(2)),
      totalInco: String(totalIncoterm.toFixed(2)),
      quantityPerSizCol: matched.quantityPerSizCol ?? '0',
      pricePerUnitIncoConverted: String(roundTo(convertedCurrValue(pricePerUnitIncoterm, recomputeContext.primaryCurrencyValue, recomputeContext.secondaryCurrencyValue), 2)),
      totalIncoConverted: String(convertedCurrValue(totalIncoterm, recomputeContext.primaryCurrencyValue, recomputeContext.secondaryCurrencyValue).toFixed(2)),
    };
  });
}

/**
 * Recomputes the template's default columns onto a landed row: Duty %, Tariff %, Freight %,
 * Agent Fee and any amount-type column. Follows the detail page, which charges duty even when the
 * row carries no freight at all.
 * @returns the row's total amount and percentage contributions to landed cost
 */
export function recomputeDefaultColumns(
  row: LandedRow,
  defaultColumns: TemplateColumnDef[],
  landedCost: number,
  testingPerUnit: number,
  setupPerUnit: number,
  primaryCurrencyValue: number,
  agentFeeRate: number,
): { totalDefaultInfoAmount: number; totalDefaultInfoPercent: number } {
  let defaultInfoAmount = 0;
  let defaultInfoPercent = 0;
  if (!Array.isArray(defaultColumns) || defaultColumns.length === 0) {
    row.totalDefaultInfoAmount = 0;
    row.totalDefaultInfoPercent = 0;
    return { totalDefaultInfoAmount: 0, totalDefaultInfoPercent: 0 };
  }

  const supplierFee = landedCost * (agentFeeRate / 100);

  defaultColumns.forEach((column) => {
    const alias = column?.aliasName;
    if (!alias) return;
    if (isSwitchedOffFreightColumn(column)) {
      if (column.type === 'percentage') row[`${alias}CalculatedValue`] = '0';
      return;
    }

    if (column.type === 'amount') {
      defaultInfoAmount += toFiniteNumber(row[alias]);
      return;
    }
    if (column.type !== 'percentage') return;

    if (FREIGHT_BASED_ALIASES.includes(alias)) {
      const percentage = toFiniteNumber(row[alias]);
      const previousCalculatedValue = toFiniteNumber(row[`${alias}CalculatedValue`]);
      const setupTestingDeduction = alias === 'dutyPercentage' ? (testingPerUnit + setupPerUnit) / (primaryCurrencyValue || 1) : 0;

      if (percentage > 0) {
        const freightContribution = resolveFreightContribution(row, defaultColumns, landedCost);
        const calculatedValue = (landedCost + freightContribution - setupTestingDeduction) * (percentage / 100);
        defaultInfoPercent += calculatedValue;
        row[`${alias}CalculatedValue`] = calculatedValue;
      } else {
        defaultInfoPercent -= previousCalculatedValue;
        row[`${alias}CalculatedValue`] = '0';
      }
      return;
    }

    if (alias === 'agentsFee') {
      row[alias] = agentFeeRate;
      row[`${alias}CalculatedValue`] = supplierFee || '0';
      defaultInfoPercent += toFiniteNumber(supplierFee);
      return;
    }

    if (alias === 'freightPercentage') {
      const freightPercentageValue = landedCost * (toFiniteNumber(row[alias]) / 100) || 0;
      row[`${alias}CalculatedValue`] = freightPercentageValue;
      defaultInfoPercent += toFiniteNumber(freightPercentageValue);
      return;
    }

    row[`${alias}CalculatedValue`] = '0';
  });

  row.totalDefaultInfoAmount = defaultInfoAmount || 0;
  row.totalDefaultInfoPercent = defaultInfoPercent || 0;
  return { totalDefaultInfoAmount: defaultInfoAmount, totalDefaultInfoPercent: defaultInfoPercent };
}

/** Looks up a royalty rate for the costing's incoterm from a license/likeness master entry. */
function resolveRoyaltyRate(masterList: RoyaltyMaster[] | undefined, masterId: string | null | undefined, incotermId: string | null): number | null {
  if (!masterId || !incotermId) return null;
  /* Compared as strings for the same ObjectId reason as getSubBrandCurrency above. */
  const wantedMasterId = toIdString(masterId as unknown as Ref | string | null);
  const master = masterList?.find((entry) => toIdString(entry?._id as unknown as Ref | string | null) === wantedMasterId);
  const matched = master?.incoterms?.find((entry) => toIdString(entry?.incotermsId as Ref | string) === incotermId);
  return matched?.value != null ? toFiniteNumber(matched.value) : null;
}

/**
 * Recomputes the template's bottom columns onto a landed row: VAT, Customer Discount, Selling Price
 * Net, royalty fees, Price Per Unit Landed, Total Landed, Gross Profit, Markup, Margin, Estimated
 * Profit and Retail Margin. Royalty rates are re-resolved from the masters, per the detail page.
 */
export function recomputeBottomColumns(
  row: LandedRow,
  template: CostingTemplateForCalc,
  amounts: { landedInfoAmount: number; landedInfoPercent: number; totalDefaultInfoAmount: number; totalDefaultInfoPercent: number },
  recomputeContext: LandedRecomputeContext,
  context: CalcContext,
  masters: CalcMasters | undefined,
  incotermId: string | null,
): void {
  const bottomColumns = template?.bottomColumnArr;
  if (!Array.isArray(bottomColumns) || bottomColumns.length === 0) return;
  const defaultColumns = template?.defaultColumns ?? [];
  const { hasRetailCurrency, retailExRate } = recomputeContext;

  bottomColumns.forEach((column) => {
    const alias = column?.aliasName;
    if (!alias) return;

    if (alias === 'pricePerUnitLanded') {
      row[alias] =
        toFiniteNumber(row.pricePerUnitIncoConverted) +
        toFiniteNumber(amounts.landedInfoAmount) +
        toFiniteNumber(amounts.landedInfoPercent) +
        toFiniteNumber(amounts.totalDefaultInfoAmount) +
        toFiniteNumber(amounts.totalDefaultInfoPercent);
      return;
    }
    if (alias === 'totalLanded') {
      row[alias] = toFiniteNumber(row.pricePerUnitLanded) * toFiniteNumber(row.quantityPerSizCol);
      return;
    }
    if (alias === 'vat') {
      const sellingPriceGross = toFiniteNumber(row.sellingPriceGross);
      row.vatCalculatedValue = sellingPriceGross - sellingPriceGross / (1 + toFiniteNumber(row.vat) / 100);
      return;
    }
    if (alias === 'customerDiscount') {
      row.customerDiscountCalculatedValue = toFiniteNumber(row.sellingPriceGross) * (toFiniteNumber(row.customerDiscount) / 100);
      return;
    }
    if (alias === 'sellingPriceNet') {
      row[alias] = toFiniteNumber(row.sellingPriceGross) - toFiniteNumber(row.vatCalculatedValue) - toFiniteNumber(row.customerDiscountCalculatedValue);

      const hasRoyaltyProduct = Boolean(context.licenseId || context.likenessId);
      if (defaultColumns.length > 0 && hasRoyaltyProduct) {
        defaultColumns
          .filter((entry) => entry?.aliasName === 'licenseRoyaltyFee' || entry?.aliasName === 'likenessRoyaltyFee')
          .forEach((entry) => {
            const isLicense = entry.aliasName === 'licenseRoyaltyFee';
            const masterId = isLicense ? context.licenseId : context.likenessId;
            const rate = resolveRoyaltyRate(isLicense ? masters?.licenseList : masters?.likenessList, masterId, incotermId);
            if (rate == null) return;
            const royaltyFee = (rate / 100) * toFiniteNumber(row.sellingPriceNet);
            row[entry.aliasName as string] = rate;
            row[`${entry.aliasName}CalculatedValue`] = royaltyFee;
            row.pricePerUnitLanded = toFiniteNumber(row.pricePerUnitLanded) + royaltyFee;
          });
      }
      row.totalLanded = toFiniteNumber(row.pricePerUnitLanded) * toFiniteNumber(row.quantityPerSizCol);
      return;
    }
    if (alias === 'grossProfitPerUnit') {
      row[alias] = toFiniteNumber(row.sellingPriceNet) - toFiniteNumber(row.pricePerUnitLanded);
      return;
    }
    if (alias === 'markupPerUnit') {
      const pricePerUnitLanded = toFiniteNumber(row.pricePerUnitLanded);
      row[alias] = pricePerUnitLanded === 0 ? 0 : toFiniteNumber(row.sellingPriceNet) / pricePerUnitLanded;
      return;
    }
    if (alias === 'marginPerUnit') {
      const sellingPriceNet = toFiniteNumber(row.sellingPriceNet);
      row[alias] = sellingPriceNet === 0 ? 0 : ((sellingPriceNet - toFiniteNumber(row.pricePerUnitLanded)) / sellingPriceNet) * 100;
      return;
    }
    if (alias === 'estimatedProfitPerUnit') {
      row[alias] = hasRetailCurrency ? toFiniteNumber(row.estimatedRetailValue) - toFiniteNumber(row.sellingPriceNet) * retailExRate : '--';
      return;
    }
    if (alias === 'retailMarginPerUnit') {
      if (!hasRetailCurrency) {
        row[alias] = '--';
        return;
      }
      const estimatedRetailValue = toFiniteNumber(row.estimatedRetailValue);
      row[alias] = estimatedRetailValue === 0 ? 0 : (toFiniteNumber(row.estimatedProfitPerUnit) / estimatedRetailValue) * 100;
    }
  });
}

/** Fields written back as strings so the saved document matches the ProductCosting schema. */
const STRINGIFIED_FIELDS = [
  'pricePerUnitIncoConverted',
  'dutyPercentageCalculatedValue',
  'tariffCostPercentageCalculatedValue',
  'agentsFeeCalculatedValue',
  'freightPercentageCalculatedValue',
  'licenseRoyaltyFeeCalculatedValue',
  'likenessRoyaltyFeeCalculatedValue',
  'totalDefaultInfoAmount',
  'totalDefaultInfoPercent',
  'pricePerUnitLanded',
  'totalLanded',
  'vatCalculatedValue',
  'customerDiscountCalculatedValue',
  'sellingPriceNet',
  'grossProfitPerUnit',
  'markupPerUnit',
  'marginPerUnit',
  'estimatedProfitPerUnit',
  'retailMarginPerUnit',
];

/**
 * Runs the full landed chain for one combination row and returns a new row.
 * Custom template columns on `landedInfo` contribute first, then the default columns, then the
 * bottom columns. The row's own `landedInfo` entries are updated in place by value only — their
 * identity fields are never touched and no entry is added or removed.
 */
export function recomputeLandedRow(
  sourceRow: LandedRow,
  recomputeContext: LandedRecomputeContext,
  template: CostingTemplateForCalc,
  context: CalcContext,
  masters: CalcMasters | undefined,
  incotermId: string | null,
): LandedRow {
  const row: LandedRow = { ...sourceRow };
  const colourwayId = resolveColourwayId(row);
  const testingPerUnit = pickSectionSum(recomputeContext.testingSum, colourwayId);
  const setupPerUnit = pickSectionSum(recomputeContext.setupSum, colourwayId);
  const landedCost = toFiniteNumber(row.pricePerUnitIncoConverted);

  let landedInfoAmount = 0;
  let landedInfoPercent = 0;
  if (Array.isArray(row.landedInfo) && row.landedInfo.length > 0) {
    row.landedInfo = row.landedInfo.map((column) => {
      if (column.type === 'amount') {
        landedInfoAmount += toFiniteNumber(column.tempValue);
        return { ...column };
      }
      if (column.type === 'percentage') {
        const calculatedValue = landedCost * (toFiniteNumber(column.tempValue) / 100);
        landedInfoPercent += calculatedValue;
        return { ...column, calculatedValue: String(calculatedValue) };
      }
      return { ...column };
    });
  }

  const { totalDefaultInfoAmount, totalDefaultInfoPercent } = recomputeDefaultColumns(
    row,
    template?.defaultColumns ?? [],
    landedCost,
    testingPerUnit,
    setupPerUnit,
    recomputeContext.primaryCurrencyValue,
    recomputeContext.agentFee,
  );

  recomputeBottomColumns(row, template, { landedInfoAmount, landedInfoPercent, totalDefaultInfoAmount, totalDefaultInfoPercent }, recomputeContext, context, masters, incotermId);

  row.pricePerUnitLanded = roundTo(toFiniteNumber(row.pricePerUnitLanded), 2) || 0;
  row.totalLanded = roundTo(toFiniteNumber(row.pricePerUnitLanded) * toFiniteNumber(row.quantityPerSizCol), 2) || 0;

  STRINGIFIED_FIELDS.forEach((field) => {
    if (field in row && row[field] != null) row[field] = String(row[field]);
  });

  return row;
}

/**
 * Recomputes every distribution centre of a costing.
 * Only the visible centre is edited by the grid, but a grand-total change moves the derived columns
 * on all of them, so all are refreshed and persisted together.
 */
export function recomputeAllDistributionCentres(
  context: CalcContext,
  grandTotal: GrandTotal,
  template: CostingTemplateForCalc,
  masters: CalcMasters | undefined,
  incotermId: string | null,
  lengthsPresent: boolean,
): DistributionCentreLanded[] {
  const centres = Array.isArray(context.landedInformations) && context.landedInformations.length > 0
    ? context.landedInformations
    : [
        {
          distributionCentreId: null,
          distributionCentreName: '--',
          distributionCentreCurrency: null,
          landedExchangeRateInformation: context.landedExchangeRateInformation ?? {},
          landedInfoArr: context.landedInformation?.landedInfoArr ?? [],
        },
      ];

  return centres.map((centre) => {
    const recomputeContext = buildLandedRecomputeContext(context, centre?.landedExchangeRateInformation, masters);
    const stamped = applyGrandTotalToLandedRows(centre?.landedInfoArr ?? [], grandTotal, context, recomputeContext, lengthsPresent);
    const respread = respreadAutomatedFreight(stamped, template?.defaultColumns ?? []);
    return { ...centre, landedInfoArr: respread.map((row) => recomputeLandedRow(row, recomputeContext, template, context, masters, incotermId)) };
  });
}

/**
 * The freight a landed row carries into the duty and tariff base. Freight %, Freight Value and
 * Freight Automated are mutually exclusive on the template, so only an active column counts — a
 * value left on the row from a column that has since been switched off would otherwise be charged
 * on top of the active one. Mirrored in the other costing engine; the parity test holds them equal.
 * @param row - The landed row.
 * @param defaultColumns - The template's default columns.
 * @param landedCost - The row's converted price per unit.
 * @returns The freight amount per unit, 0 when no active freight column has a value.
 */
export function resolveFreightContribution(
  row: Record<string, unknown>,
  defaultColumns: ReadonlyArray<{ aliasName?: string; isActive?: boolean }>,
  landedCost: number,
): number {
  const active = new Set(defaultColumns.filter((column) => column?.isActive !== false).map((column) => column?.aliasName));
  if (active.has('freightAutomated') && toFiniteNumber(row['freightAutomated'])) return toFiniteNumber(row['freightAutomated']);
  if (active.has('freightValue') && toFiniteNumber(row['freightValue'])) return toFiniteNumber(row['freightValue']);
  if (active.has('freightPercentage') && toFiniteNumber(row['freightPercentage'])) return landedCost * (toFiniteNumber(row['freightPercentage']) / 100);
  return 0;
}

/** Decimal places a re-spread per-unit automated freight is kept to, matching the detail page. */
const AUTOMATED_FREIGHT_PER_UNIT_DECIMALS = 4;

/**
 * Re-spreads Freight Automated over the centre's current quantities. The detail page saves the
 * product's freight for the centre (`freightAutomatedTotal`, already in the centre's currency)
 * beside the per-unit amount, so when a quantity changes here the per-unit amount is total ÷ the
 * new total quantity — the sheet's `F20 = E20/B20` — instead of keeping the figure spread over
 * the old quantity. Rows saved without a total are left as they are. While the column is switched
 * off, a figure left on a row from when it was on is cleared, as the detail page clears it, so it
 * stops adding to the landed price. Mirrored in the other
 * costing engine; the parity test holds them equal.
 * @param rows - The centre's landed rows, quantities already stamped.
 * @param defaultColumns - The template's default columns.
 * @returns The rows with the per-unit freight re-spread or cleared, or the same rows when it does not apply.
 */
export function respreadAutomatedFreight<TRow extends Record<string, unknown>>(
  rows: TRow[],
  defaultColumns: ReadonlyArray<{ aliasName?: string; isActive?: boolean }>,
): TRow[] {
  const automatedColumn = defaultColumns.find((column) => column?.aliasName === 'freightAutomated');
  if (!automatedColumn) return rows;
  if (automatedColumn.isActive === false) {
    return rows.map((row) =>
      toFiniteNumber(row['freightAutomated']) || toFiniteNumber(row['freightAutomatedTotal']) ? { ...row, freightAutomated: '0', freightAutomatedTotal: '0' } : row,
    );
  }
  const totalQuantity = rows.reduce((sum, row) => {
    const quantity = toFiniteNumber(row['quantityPerSizCol']);
    return quantity > 0 ? sum + quantity : sum;
  }, 0);
  const factor = 10 ** AUTOMATED_FREIGHT_PER_UNIT_DECIMALS;
  return rows.map((row) => {
    const total = toFiniteNumber(row['freightAutomatedTotal']);
    if (!total) return row;
    const perUnit = totalQuantity > 0 ? Math.round((total / totalQuantity + Number.EPSILON) * factor) / factor : 0;
    return { ...row, freightAutomated: String(perUnit) };
  });
}


/**
 * Whether a default column is a freight column the template has switched off. Switching freight
 * columns only flips their active flag, so a figure entered while one was on stays on the row; it
 * must not be charged in the landed price once another freight column is active. Mirrored in every
 * costing engine; the parity tests hold them equal.
 * @param column - The template default column.
 * @returns True for Freight %, Freight Value or Freight Automated when inactive.
 */
export function isSwitchedOffFreightColumn(column: { aliasName?: unknown; isActive?: boolean } | undefined): boolean {
  return FREIGHT_TOGGLE_ALIASES.includes(String(column?.aliasName)) && column?.isActive === false;
}
